import React from 'react';
import { useTranslation } from 'react-i18next';
import { SupplierPreference } from '@api/generated/search-service';
import SvgFavorites from '@components/icons/Favorites.tsx';
import SvgFavoritesFilled from '@components/icons/FavoritesFilled.tsx';
import SvgExclamationTriangle from '@components/icons/ExclamationTriangle.tsx';

interface SupplierPreferenceToggleProps {
  preference?: SupplierPreference;
  onChange: (preference: SupplierPreference | null) => void;
}

/**
 * Označení dodavatele u produktu. Srdíčko a negativní označení se vylučují —
 * kliknutí na už aktivní ikonu označení zruší (neutrální stav).
 *
 * Je uvnitř <label> s radiem pro výběr varianty, proto se kliknutí musí zastavit,
 * ať se označením omylem nepřepne vybraná cenová varianta.
 */
const SupplierPreferenceToggle: React.FC<SupplierPreferenceToggleProps> = ({ preference, onChange }) => {
  const { t } = useTranslation('client');

  const isPreferred = preference === SupplierPreference.Preferred;
  const isNonPreferred = preference === SupplierPreference.NonPreferred;

  const handle = (e: React.MouseEvent, next: SupplierPreference) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(preference === next ? null : next);
  };

  return (
    <div className="flex flex-row items-center gap-1">
      <button
        type="button"
        className={`h-5 w-5 flex items-center justify-center cursor-pointer ${isPreferred ? 'text-controls-link-light-primary-default' : 'text-light'}`}
        title={isPreferred ? t('productDetail.unmarkPreferredSupplier') : t('productDetail.markPreferredSupplier')}
        aria-pressed={isPreferred}
        onClick={e => handle(e, SupplierPreference.Preferred)}
      >
        {isPreferred ? <SvgFavoritesFilled className="w-full h-full" /> : <SvgFavorites className="w-full h-full" />}
      </button>
      <button
        type="button"
        className={`h-5 w-5 flex items-center justify-center cursor-pointer ${isNonPreferred ? 'text-[#da1e28]' : 'text-light'}`}
        title={isNonPreferred ? t('productDetail.unmarkNonPreferredSupplier') : t('productDetail.markNonPreferredSupplier')}
        aria-pressed={isNonPreferred}
        onClick={e => handle(e, SupplierPreference.NonPreferred)}
      >
        <SvgExclamationTriangle className="w-full h-full" />
      </button>
    </div>
  );
};

export default SupplierPreferenceToggle;
